'use server';

import { createAdminClient, createClient } from '@/lib/supabase/server';
import { requirePermission, computeEffectivePermissions } from '@/lib/permissions';
import { requireStaff } from '@/lib/requireStaff';
import { createStaffMember } from '@/actions/staff';
import { createStudent } from '@/actions/students';
import { sendAccessRequestDeclinedEmail } from '@/lib/accountEmails';
import { verifyCaptcha } from '@/actions/auth';
import { isRequestableStaffTitle } from '@/lib/permissionTypes';
import { notifyStaffWithCapability } from '@/actions/notifications';
import { clientRateLimitKey, consumeRateLimit, RATE_LIMIT_TOO_MANY } from '@/lib/rateLimit';

/** Student requests need manage_students, staff requests need
 * manage_staff_accounts - lab_supervisor/production_manager hold every
 * capability automatically (computeEffectivePermissions), so this also
 * covers the top-role case without a separate branch. */
async function requireAccessRequestScope(requestingAs: 'student' | 'staff'): Promise<void> {
  await requirePermission(requestingAs === 'student' ? 'manage_students' : 'manage_staff_accounts');
}

export interface AccessRequestInput {
  firstName: string;
  middleName?: string;
  preferredName?: string;
  lastName: string;
  email: string;
  phone?: string;
  requestingAs: 'student' | 'staff';
  /** Required when requestingAs is staff. Ignored for students. */
  requestedTitle?: string;
  /** Turnstile token from the request-access form (2026-08-22 security audit - 
   * this was previously the one public form with zero bot protection). */
  captchaToken?: string;
}

export async function submitAccessRequest(input: AccessRequestInput): Promise<
  { success: true } | { error: string }
> {
  // Validate required fields
  if (!input.firstName?.trim()) return { error: 'First name is required.' };
  if (!input.lastName?.trim()) return { error: 'Last name is required.' };
  if (!input.email?.trim()) return { error: 'Email is required.' };
  if (!input.requestingAs) return { error: 'Please select Student or Staff.' };

  const ip = await clientRateLimitKey();
  if (!(await consumeRateLimit({ bucket: 'access_request', key: ip, limit: 5, windowSec: 60 * 60 }))) {
    return { error: RATE_LIMIT_TOO_MANY };
  }

  let requestedTitle: string | null = null;
  if (input.requestingAs === 'staff') {
    const title = input.requestedTitle?.trim() ?? '';
    if (!isRequestableStaffTitle(title)) {
      return { error: 'Please choose a staff title.' };
    }
    requestedTitle = title;
  }

  const MAX_NAME_LEN = 100;
  const MAX_PHONE_LEN = 30;
  if (input.firstName.trim().length > MAX_NAME_LEN) return { error: 'First name is too long.' };
  if (input.middleName && input.middleName.trim().length > MAX_NAME_LEN) return { error: 'Middle name is too long.' };
  if (input.preferredName && input.preferredName.trim().length > MAX_NAME_LEN) return { error: 'Preferred name is too long.' };
  if (input.lastName.trim().length > MAX_NAME_LEN) return { error: 'Last name is too long.' };
  if (input.phone && input.phone.trim().length > MAX_PHONE_LEN) return { error: 'Phone number is too long.' };

  // CAPTCHA is required whenever the server has Turnstile configured at all - 
  // previously `if (input.captchaToken)` skipped verification entirely when
  // the client omitted a token, which is trivial by calling this Server
  // Action directly rather than through the form (2026-08-22 audit finding).
  // Mirrors the client's own `TURNSTILE_SITEKEY &&` gate: an environment that
  // genuinely has no Turnstile secret configured (e.g. a preview env without
  // its own site) still degrades gracefully rather than hard-failing.
  if (process.env.TURNSTILE_SECRET_KEY) {
    if (!input.captchaToken) return { error: 'Complete the CAPTCHA challenge first.' };
    const { success } = await verifyCaptcha(input.captchaToken);
    if (!success) return { error: 'CAPTCHA verification failed. Please try again.' };
  }

  // Validate email format
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(input.email.trim())) {
    return { error: 'Please enter a valid email address.' };
  }

  const supabase = await createAdminClient();
  const email = input.email.trim().toLowerCase();

  // Only an unresolved (pending) or already-approved request should block a
  // resubmission - a declined request means the circumstances may have
  // changed, and the person deserves a chance to correct/resubmit rather
  // than being locked out for 24h with a message that implies something is
  // still under review.
  const oneDayAgo = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
  const { data: recentRequests } = await supabase
    .from('access_requests')
    .select('status')
    // .eq, not .ilike - `email` is already lowercased above, so ilike bought
    // nothing here except letting a crafted `%`/`_` in a public form field
    // act as a SQL wildcard instead of a literal character (2026-08-22 audit).
    .eq('email', email)
    .gte('created_at', oneDayAgo)
    .in('status', ['pending', 'approved'])
    .order('created_at', { ascending: false })
    .limit(1);

  const recent = recentRequests?.[0];
  if (recent?.status === 'approved') {
    return {
      error: 'This email already has access. Try logging in, or use "Forgot password" if you need a new one.',
    };
  }
  if (recent?.status === 'pending') {
    return {
      error: 'You already submitted a request recently. Check your email for updates, or contact the equipment desk.',
    };
  }

  // Insert the access request
  const { data: inserted, error } = await supabase
    .from('access_requests')
    .insert({
      first_name: input.firstName.trim(),
      middle_name: input.middleName?.trim() || null,
      preferred_name: input.preferredName?.trim() || null,
      last_name: input.lastName.trim(),
      email,
      phone: input.phone?.trim() || null,
      vid: null,
      requesting_as: input.requestingAs,
      requested_title: requestedTitle,
    })
    .select('id')
    .single();

  if (error) {
    console.error('Access request submission error:', error);
    return { error: 'Could not submit your request. Please try again.' };
  }

  // Bell only for people who can approve this kind of request.
  await notifyStaffWithCapability(
    input.requestingAs === 'student' ? 'manage_students' : 'manage_staff_accounts',
    'access_request',
    `New ${input.requestingAs} access request from ${input.firstName.trim()} ${input.lastName.trim()} (${email})${requestedTitle ? ` - ${requestedTitle}` : ''}`,
    { requestId: inserted?.id ?? null },
  );

  return { success: true };
}

export interface AccessRequestRow {
  id: string;
  first_name: string;
  middle_name: string | null;
  preferred_name: string | null;
  last_name: string;
  email: string;
  phone: string | null;
  requesting_as: 'student' | 'staff';
  requested_title: string | null;
  created_at: string | null;
  status: string;
  /** Only populated for lab_supervisor/production_manager callers - everyone
   * else gets undefined even though the underlying row has reviewed_by set. */
  reviewedByName?: string;
}

export async function listPendingAccessRequests(
  requestingAs?: 'student' | 'staff'
): Promise<AccessRequestRow[]> {
  if (requestingAs) await requireAccessRequestScope(requestingAs);
  else await requirePermission('manage_staff_accounts');
  const supabase = await createAdminClient();
  let query = supabase
    .from('access_requests')
    .select('id, first_name, middle_name, preferred_name, last_name, email, phone, requesting_as, requested_title, created_at, status')
    .eq('status', 'pending')
    .order('created_at', { ascending: true });
  if (requestingAs) query = query.eq('requesting_as', requestingAs);
  const { data, error } = await query;
  if (error) throw new Error(error.message);
  return (data ?? []) as AccessRequestRow[];
}

export async function approveAccessRequest(id: string): Promise<{ error?: string }> {
  await requireStaff(); // cheap baseline gate before the row lookup below
  const supabase = await createAdminClient();
  const session = await createClient();
  const { data: { user } } = await session.auth.getUser();

  const { data: req, error: loadError } = await supabase
    .from('access_requests')
    .select('id, first_name, middle_name, preferred_name, last_name, email, phone, requesting_as, requested_title, status')
    .eq('id', id)
    .single();
  if (loadError || !req) return { error: 'Request not found.' };
  if (req.status !== 'pending') return { error: 'This request was already reviewed.' };

  await requireAccessRequestScope(req.requesting_as as 'student' | 'staff');

  try {
    if (req.requesting_as === 'staff') {
      await createStaffMember({
        first_name: req.first_name,
        last_name: req.last_name,
        email: req.email,
        title: isRequestableStaffTitle(req.requested_title) ? req.requested_title : null,
      });
    } else {
      await createStudent({
        first_name: req.first_name,
        middle_name: req.middle_name ?? undefined,
        preferred_name: req.preferred_name ?? undefined,
        last_name: req.last_name,
        email: req.email,
        phone: req.phone ?? undefined,
      });
    }
  } catch (e) {
    return { error: (e as { message?: string }).message ?? 'Could not create the account.' };
  }

  const { error } = await supabase
    .from('access_requests')
    .update({
      status: 'approved',
      reviewed_at: new Date().toISOString(),
      reviewed_by: user?.id ?? null,
    })
    .eq('id', id);
  if (error) return { error: error.message };
  return {};
}

export async function declineAccessRequest(id: string): Promise<{ error?: string }> {
  await requireStaff();
  const supabase = await createAdminClient();
  const session = await createClient();
  const { data: { user } } = await session.auth.getUser();

  const { data: req, error: loadError } = await supabase
    .from('access_requests')
    .select('id, first_name, email, requesting_as, status')
    .eq('id', id)
    .single();
  if (loadError || !req) return { error: 'Request not found.' };
  if (req.status !== 'pending') return { error: 'This request was already reviewed.' };

  await requireAccessRequestScope(req.requesting_as as 'student' | 'staff');

  const { error } = await supabase
    .from('access_requests')
    .update({
      status: 'declined',
      reviewed_at: new Date().toISOString(),
      reviewed_by: user?.id ?? null,
    })
    .eq('id', id)
    .eq('status', 'pending');
  if (error) return { error: error.message };

  // Best-effort notification - the decline itself already succeeded above.
  try {
    await sendAccessRequestDeclinedEmail({ email: req.email, firstName: req.first_name });
  } catch (e) {
    console.error('[access-requests] decline email failed (non-fatal):', e);
  }

  return {};
}

/** Recently resolved requests (approved or declined) for one scope, most
 * recent first - for a "recently reviewed" strip so staff can reconsider a
 * wrong-role decline or clear the list, without querying the pending queue.
 * reviewedByName is only attached for lab_supervisor/production_manager - 
 * capability holders (manage_students/manage_staff_accounts) can review the
 * same list but don't get to see who made the call. */
export async function listReviewedAccessRequests(requestingAs: 'student' | 'staff'): Promise<AccessRequestRow[]> {
  await requireAccessRequestScope(requestingAs);
  const supabase = await createAdminClient();
  const { data, error } = await supabase
    .from('access_requests')
    .select('id, first_name, middle_name, preferred_name, last_name, email, phone, requesting_as, requested_title, created_at, status, reviewed_by')
    .eq('requesting_as', requestingAs)
    .in('status', ['approved', 'declined'])
    .order('reviewed_at', { ascending: false })
    .limit(25);
  if (error) throw new Error(error.message);
  const rows = data ?? [];

  const session = await createClient();
  const { data: { user } } = await session.auth.getUser();
  const { isSuperuser } = user ? await computeEffectivePermissions(session, user.id) : { isSuperuser: false };

  let reviewerNames = new Map<string, string>();
  if (isSuperuser) {
    const reviewerIds = [...new Set(rows.map(r => r.reviewed_by).filter((v): v is string => !!v))];
    if (reviewerIds.length > 0) {
      const { data: reviewers } = await supabase
        .from('profiles')
        .select('id, first_name, last_name')
        .in('id', reviewerIds);
      reviewerNames = new Map((reviewers ?? []).map(r => [r.id, `${r.first_name} ${r.last_name}`.trim()]));
    }
  }

  return rows.map(({ reviewed_by, ...r }) => ({
    ...r,
    reviewedByName: isSuperuser ? (reviewed_by ? reviewerNames.get(reviewed_by) ?? 'Unknown' : undefined) : undefined,
  })) as AccessRequestRow[];
}

/** Reopens a declined request with a corrected student/staff selection - 
 * covers the "picked the wrong role by mistake" case without forcing the
 * requester to resubmit from scratch (a resubmit would also work now that
 * declined rows no longer block it, but this keeps the original request/notes
 * intact and is faster for staff). Gated on the request's original scope. */
export async function reconsiderAccessRequest(
  id: string,
  correctedRequestingAs: 'student' | 'staff'
): Promise<{ error?: string }> {
  await requireStaff();
  const supabase = await createAdminClient();

  const { data: req, error: loadError } = await supabase
    .from('access_requests')
    .select('id, requesting_as, status')
    .eq('id', id)
    .single();
  if (loadError || !req) return { error: 'Request not found.' };
  if (req.status !== 'declined') return { error: 'Only a declined request can be reconsidered.' };

  // Reconsidering changes the row's scope (requesting_as) - check both the
  // original AND the target scope's permission, not just the original.
  // Without this, a manage_students-only reviewer could relabel a declined
  // request as 'staff', silently moving it into a queue they can't act on
  // (2026-08-22 deep-audit finding - not itself a privilege grant since
  // approveAccessRequest re-checks the updated row's scope, but a real
  // authorization-consistency gap).
  await requireAccessRequestScope(req.requesting_as as 'student' | 'staff');
  await requireAccessRequestScope(correctedRequestingAs);

  const { error } = await supabase
    .from('access_requests')
    .update({
      status: 'pending',
      requesting_as: correctedRequestingAs,
      ...(correctedRequestingAs === 'student' ? { requested_title: null } : {}),
      reviewed_at: null,
      reviewed_by: null,
    })
    .eq('id', id)
    .eq('status', 'declined');
  if (error) return { error: error.message };
  return {};
}

/** Deletes every resolved (approved/declined) request in one scope - nothing
 * else in the schema references access_requests.id, so this is safe cleanup
 * with no cascading effects on accounts. Manual/on-demand only, matching the
 * rest of the housekeeping tooling (no silent auto-delete). */
export async function clearReviewedAccessRequests(
  requestingAs: 'student' | 'staff'
): Promise<{ error?: string; count?: number }> {
  await requireAccessRequestScope(requestingAs);
  const supabase = await createAdminClient();
  const { data, error } = await supabase
    .from('access_requests')
    .delete()
    .eq('requesting_as', requestingAs)
    .in('status', ['approved', 'declined'])
    .select('id');
  if (error) return { error: error.message };
  return { count: data?.length ?? 0 };
}
